import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Alert, CircularProgress } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { SurveyInstanceLayout } from '../layouts/SurveyInstanceLayout';
import { QRCodeDisplay } from '../components/QRCodeDisplay';
import { InstanceList } from '../components/InstanceList';
import { useSurveyInstances, useCreateSurveyInstance } from '../hooks/useSurveyInstances';
import { SurveyInstanceDTO } from '../dtos';
import { usePostHog } from '../../../hooks/usePostHog';
import { SURVEY_INSTANCE_EVENTS } from '../../../utils/trackingEvents';

export const SurveyInstancePage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { captureEvent } = usePostHog();
  const [selectedInstance, setSelectedInstance] = useState<SurveyInstanceDTO | null>(null);
  const [qrDisplayStartTime, setQrDisplayStartTime] = useState<number | null>(null);

  // Hooks
  const {
    data: instancesData,
    isLoading: isLoadingInstances,
    error: instancesError,
    refetch: refetchInstances,
  } = useSurveyInstances(surveyId || '', 5);

  const createInstanceMutation = useCreateSurveyInstance(surveyId || '');

  // Track instance list view on page load
  useEffect(() => {
    if (instancesData?.data?.instances && surveyId) {
      const allInstances = instancesData.data.instances;
      // Only include instances created in the last 24 hours for analytics
      const now = Date.now();
      const analyticsInstances = allInstances.filter((instance) => {
        if (!instance.createdAt) return false;
        const createdAtTime = new Date(instance.createdAt).getTime();
        return now - createdAtTime < 24 * 60 * 60 * 1000;
      });
      const surveyTitle = allInstances[0]?.instanceData?.survey?.title || 'Unknown';

      captureEvent(SURVEY_INSTANCE_EVENTS.INSTANCE_LIST_VIEWED, {
        survey_id: surveyId,
        survey_title: surveyTitle,
        instances_count: analyticsInstances.length,
        total_responses: analyticsInstances.reduce(
          (sum, instance) => sum + (instance.responseCount || 0),
          0
        ),
      });
    }
  }, [instancesData, surveyId, captureEvent]);

  // Handlers
  const handleCreateInstance = async () => {
    if (!surveyId) return;

    try {
      const result = await createInstanceMutation.mutateAsync({ surveyId });
      if (result.data) {
        const instance = result.data.instance;
        const surveyTitle = instance.instanceData?.survey?.title || 'Unknown';

        // Track instance creation
        captureEvent(SURVEY_INSTANCE_EVENTS.INSTANCE_CREATED, {
          survey_id: surveyId,
          survey_title: surveyTitle,
          instance_id: instance.id,
          auto_selected: true,
        });

        setSelectedInstance(instance);
        refetchInstances();
      }
    } catch (error) {
      console.error('Error creating instance:', error);
    }
  };

  const handleSelectInstance = (instance: SurveyInstanceDTO) => {
    const surveyTitle = instance.instanceData?.survey?.title || 'Unknown';

    // Track instance selection
    captureEvent(SURVEY_INSTANCE_EVENTS.INSTANCE_SELECTED, {
      survey_id: surveyId || '',
      survey_title: surveyTitle,
      instance_id: instance.id,
      response_count: instance.responseCount || 0,
      selection_method: 'manual',
    });

    setSelectedInstance(instance);
    setQrDisplayStartTime(Date.now());
  };

  const handleRefresh = () => {
    // Track instance refresh
    captureEvent(SURVEY_INSTANCE_EVENTS.INSTANCE_REFRESHED, {
      survey_id: surveyId || '',
      has_selected_instance: !!selectedInstance,
      selected_instance_id: selectedInstance?.id,
    });

    refetchInstances();
  };

  // Track QR code display when instance is selected
  useEffect(() => {
    if (selectedInstance && qrDisplayStartTime) {
      const surveyTitle = selectedInstance.instanceData?.survey?.title || 'Unknown';

      captureEvent(SURVEY_INSTANCE_EVENTS.INSTANCE_QR_DISPLAYED, {
        survey_id: surveyId || '',
        survey_title: surveyTitle,
        instance_id: selectedInstance.id,
        qr_size: 600,
        fill_space_enabled: true,
        access_link: selectedInstance.accessLink,
      });
    }

    // Cleanup function to track display duration when component unmounts or instance changes
    return () => {
      if (selectedInstance && qrDisplayStartTime) {
        const displayDuration = (Date.now() - qrDisplayStartTime) / 1000; // in seconds

        captureEvent('survey_instance_qr_display_ended', {
          survey_id: surveyId || '',
          instance_id: selectedInstance.id,
          display_duration_seconds: displayDuration,
        });
      }
    };
  }, [selectedInstance, qrDisplayStartTime, surveyId, captureEvent]);

  // Loading state
  if (isLoadingInstances) {
    return (
      <SurveyInstanceLayout title="Loading...">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={60} />
        </Box>
      </SurveyInstanceLayout>
    );
  }

  // Error state
  if (instancesError) {
    return (
      <SurveyInstanceLayout title="Error">
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load survey instances. Please try again.
        </Alert>
        <Button variant="contained" onClick={handleRefresh}>
          Retry
        </Button>
      </SurveyInstanceLayout>
    );
  }

  // Filter instances to show only those created within the last 12 hours
  const allInstances = instancesData?.data?.instances || [];
  const instances = allInstances.filter((instance) => {
    if (!instance.createdAt) return false;
    const createdAtTime = new Date(instance.createdAt).getTime();
    const now = Date.now();
    const twelveHoursInMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    return now - createdAtTime < twelveHoursInMs;
  });

  const surveyTitle = allInstances[0]?.instanceData?.survey?.title || 'Survey';

  return (
    <SurveyInstanceLayout title={surveyTitle}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 200px)', // More space for content
          maxHeight: '1400px', // Much higher max for large displays
          overflow: 'hidden',
        }}
      >
        {/* Main Content Area */}
        {selectedInstance ? (
          // When instance is selected - show QR code full screen
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              height: '100%',
              overflow: 'hidden', // Prevent QR code from overflowing
            }}
          >
            <QRCodeDisplay
              url={`${window.location.origin}${selectedInstance.accessLink}`}
              title="Scan to Answer Survey"
              fillSpace={true} // Enable dynamic sizing to fill available space
              size={600} // Larger fallback size for presentations
            />
          </Box>
        ) : (
          // When no instance selected - show create button and list
          <>
            {/* Create Button Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                flexShrink: 0, // Prevent shrinking
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateInstance}
                disabled={createInstanceMutation.isPending}
                sx={{ textTransform: 'none', py: 1.5, px: 3 }}
              >
                {createInstanceMutation.isPending ? 'Creating...' : 'Create New Instance'}
              </Button>
            </Box>

            {/* Controls Section - Only show when no instance selected */}
            <Box
              sx={{
                pt: 3,
                borderTop: 1,
                borderColor: 'divider',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // Prevent overflow
              }}
            >
              {/* Refresh Button */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, px: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleRefresh}
                  startIcon={<RefreshIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Box>

              {/* Instance List with scroll container */}
              <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pb: 2 }}>
                <InstanceList
                  instances={instances}
                  onSelectInstance={handleSelectInstance}
                  selectedInstanceId={undefined}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </SurveyInstanceLayout>
  );
};
